import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { guardrailApi, type Guardrail } from '@/lib/api';
import styles from './_auth.guardrails.module.css';
import { PageGuide } from '@/components/PageGuide';


export const Route = createFileRoute('/_auth/guardrails')({
  component: GuardrailsPage,
});


const SEVERITY_COLORS = { LOW: '#10b981', MEDIUM: '#f59e0b', HIGH: '#ef4444', CRITICAL: '#ff2d55' };
const SEVERITY_BG = { LOW: 'rgba(16,185,129,0.1)', MEDIUM: 'rgba(245,158,11,0.1)', HIGH: 'rgba(239,68,68,0.1)', CRITICAL: 'rgba(255,45,85,0.1)' };

export default function GuardrailsPage() {
  const qc = useQueryClient();
  const { data: guardrails = [], isLoading } = useQuery({ queryKey: ['guardrails'], queryFn: guardrailApi.list });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    ruleType: 'KEYWORD',
    severity: 'MEDIUM' as Guardrail['severity'],
    pattern: '',
  });

  const createMutation = useMutation({
    mutationFn: (body: Partial<Guardrail>) => guardrailApi.create(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['guardrails'] }); setShowForm(false); },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => guardrailApi.update(id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['guardrails'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => guardrailApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['guardrails'] }),
  });

  return (
    <div className={styles.page}>
      <PageGuide pageKey="guardrails" />
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Guardrails</h1>
          <p className={styles.subtitle}>Content safety rules applied to all agent responses</p>
        </div>
        <button className={styles.primaryBtn} onClick={() => setShowForm(true)}>+ New Guardrail</button>
      </div>

      {showForm && (
        <div className={styles.formCard}>
          <h3>Create Guardrail</h3>
          <div className={styles.formGrid}>
            <label>Name
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Block PII disclosure" />
            </label>
            <label>Description
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </label>
            <label>Rule Type
              <select value={form.ruleType} onChange={e => setForm(f => ({ ...f, ruleType: e.target.value }))}>
                <option value="KEYWORD">Keyword list (JSON array)</option>
                <option value="REGEX">Regular expression</option>
              </select>
            </label>
            <label>Severity
              <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value as Guardrail['severity'] }))}>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </label>
            <label className={styles.fullWidth}>
              Pattern
              <input
                value={form.pattern}
                onChange={e => setForm(f => ({ ...f, pattern: e.target.value }))}
                placeholder={form.ruleType === 'KEYWORD' ? '["ssn","credit card","password"]' : '^.*\\bpassword\\b.*$'}
                className={styles.mono}
              />
            </label>
          </div>
          <div className={styles.formActions}>
            <button className={styles.secondaryBtn} onClick={() => setShowForm(false)}>Cancel</button>
            <button className={styles.primaryBtn} onClick={() => createMutation.mutate(form as Partial<Guardrail>)}>Create</button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className={styles.loading}>Loading guardrails…</div>
      ) : guardrails.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>🛡️</div>
          <p>No guardrails configured. Add rules to protect your users from harmful AI responses.</p>
        </div>
      ) : (
        <div className={styles.list}>
          {guardrails.map(g => (
            <div key={g.id} className={`${styles.guardrailRow} ${!g.active ? styles.inactive : ''}`}>
              <div className={styles.guardrailLeft}>
                <div className={styles.guardrailHeader}>
                  <span
                    className={styles.severityBadge}
                    style={{
                      color: SEVERITY_COLORS[g.severity],
                      background: SEVERITY_BG[g.severity],
                    }}
                  >
                    {g.severity}
                  </span>
                  <span className={styles.typeBadge}>{g.ruleType}</span>
                  {!g.active && <span className={styles.disabledBadge}>DISABLED</span>}
                </div>
                <h3 className={styles.guardrailName}>{g.name}</h3>
                {g.description && <p className={styles.guardrailDesc}>{g.description}</p>}
                {g.pattern && (
                  <code className={styles.pattern}>
                    {g.ruleType === 'KEYWORD' ? `Keywords: ${g.pattern}` : `Regex: ${g.pattern}`}
                  </code>
                )}
              </div>
              <div className={styles.guardrailActions}>
                <button
                  className={g.active ? styles.disableBtn : styles.enableBtn}
                  onClick={() => toggleMutation.mutate({ id: g.id, active: !g.active })}
                >
                  {g.active ? 'Disable' : 'Enable'}
                </button>
                <button className={styles.deleteBtn} onClick={() => deleteMutation.mutate(g.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
